import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "react-query";
import { ApiError, fetchApi } from "../helpers/reactQueryApi";
import {
  Activity,
  ActivityCreation,
  ActivitystreamFilter,
  FileAttachment,
} from "../@types/activities";
import { includes } from "lodash";
import { stringify } from "query-string";

// Mirrors app/views/activitystreams/index.json.ruby: only slug/name/can are
// present on all three stream kinds — the meta "all" stream carries just
// meta + can.create, network streams have no group/archive flags, and only
// group streams ship group_slug/archived/category_name etc.
export type Activitystream = {
  slug: string;
  name: string;
  category_name?: string;
  group_slug?: string;
  externally_visible?: boolean;
  meta?: boolean;
  can?: {
    create?: boolean;
    email_recommend_activity_internally?: boolean;
    email_recommend_activity_externally?: boolean;
    create_appointment_activity?: boolean;
  };
  subscriber_count?: number;
  hidden?: boolean;
  closed?: boolean;
  sensitive?: boolean;
  archived?: boolean;
};

/*
 * This hook is currently not used in activitystreams/Userbar to deduplicate the /activitystreams.json request.
 */
export function useActivitystreams(params?: {
  includeSubscriberCount?: boolean;
}) {
  const includeSubscriberCount = !!params?.includeSubscriberCount;
  return useQuery<Activitystream[], Error>(
    ["activitystreams", { includeSubscriberCount }],
    () =>
      fetchApi(
        `/activitystreams.json${
          includeSubscriberCount
            ? `?${stringify({
                include_subscriber_count: true,
              })}`
            : ""
        }`,
      ),
    { staleTime: Infinity },
  );
}

type UseActivitiesOptions = {
  limit?: number;
  offset?: number;
  filter?: ActivitystreamFilter;
  last_fetched_at?: string;
  member?: string;
  likes?: string;
  tag?: string;
};

export function useActivities(
  streamUrl: string,
  options: UseActivitiesOptions = {},
) {
  const { limit = 20, filter, last_fetched_at, member, likes, tag } = options;

  return useInfiniteQuery({
    queryKey: ["activities", streamUrl, options],
    queryFn: async ({ pageParam }) => {
      let url = pageParam;

      if (!url) {
        const params = new URLSearchParams();
        params.set("per_page", limit.toString());
        params.set("offset", "0");

        if (last_fetched_at) params.set("last_fetched_at", last_fetched_at);
        if (member) params.set("member", member);
        if (likes) params.set("likes", likes);
        if (tag) params.set("tag", tag);

        if (filter && !includes(["all", "not_sticky"], filter))
          url = `${streamUrl}/${filter}/activities?${params.toString()}`;
        else url = `${streamUrl}/activities?${params.toString()}`;
      }

      return fetchApi(url, { parseLinkHeaders: true });
    },
    getNextPageParam: (lastPage) => lastPage.nextPageUrl,
    enabled: !!streamUrl,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useCreateActivity(
  options: UseMutationOptions<Activity, Error, ActivityCreation> = {},
) {
  return useMutation(
    (body) =>
      fetchApi("/activities", {
        method: "POST",
        body,
      }),
    options,
  );
}
export function useUpdateActivity(
  options: UseMutationOptions<
    Activity,
    Error,
    { id: string; body: ActivityCreation }
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, body }) =>
      fetchApi(`/activities/${id}`, {
        method: "PUT",
        body,
      }),
    {
      ...options,
      onSuccess: (...args) => {
        // Drop the edit-mode cache entry so the next Bearbeiten click
        // refetches fresh. The save response is served in show mode (no
        // translations), so we can't simply patch `?edit=true` with it;
        // removing forces a fresh GET on reopen. The show-mode cache is
        // still updated by the composer's own onSuccess handler
        // (`queryClient.setQueryData("/activities/:id", data)`).
        const [data] = args;
        queryClient.removeQueries(`/activities/${data.id}?edit=true`);
        options?.onSuccess?.(...args);
      },
    },
  );
}

export function useActivity(
  activityId?: string,
  { edit }: { edit?: boolean } = {},
) {
  const url = `/activities/${activityId}${edit ? "?edit=true" : ""}`;
  return useQuery<Activity>(url, {
    enabled: !!activityId,
    retry: (_, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500),
    refetchOnWindowFocus: (query) => query.state.status !== "error",
  });
}

export function useActivityViewCount(activityId: string, enabled: boolean) {
  return useQuery<{
    view_count: number | null;
    email_delivery_count: number | null;
    reach_count: number | null;
  }>(`/activities/${activityId}/view_count`, {
    enabled,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

export function useStickyActivities(streamUrl?: string) {
  return useQuery<Activity[]>(`${streamUrl}/sticky/activities`, {
    enabled: !!streamUrl,
    refetchOnWindowFocus: false,
  });
}

export function useFileToken(
  options: UseMutationOptions<{ token: string }, Error, string> = {},
) {
  return useMutation(
    (fileId: string) => fetchApi(`/api/storage/files/${fileId}/token`),
    options,
  );
}

export function useFile(
  options: UseMutationOptions<FileAttachment, Error, string> = {},
) {
  return useMutation(
    (fileId: string) => fetchApi(`/api/storage/files/${fileId}`),
    options,
  );
}

type UseCreateVoteArgs = {
  activityId: string;
  body: {
    option_ids?: string[];
    proposal_ids?: string[];
    custom_options?: string[];
    custom_option_translations?: Record<string, Record<string, string>>;
    custom_proposal?: { date: string; time: string; time_end: string };
  };
};

export function useCreateVote(
  options: UseMutationOptions<Activity, Error, UseCreateVoteArgs> = {},
) {
  return useMutation(
    ({ activityId, body }: UseCreateVoteArgs) =>
      fetchApi(`/api/v1/activities/${activityId}/vote`, {
        method: "PUT",
        body,
      }),
    options,
  );
}
